import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import CloseIcon from '@mui/icons-material/Close';
import CircularProgress from '@mui/material/CircularProgress';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import TemplateCard from '../../TemplateCard/TemplateCard';
import TourButton from '../../TourButton/TourButton';
import { getTemplatePickerModalSteps } from '../../../tours/templatePickerModal';
import './TemplatePickerModal.css';

const CALORIC_BUCKETS = [
    '<800', '800-1100', '1100-1400', '1400-1700', '1700-2000',
    '2000-2300', '2300-2600', '2600-2900', '2900-3200',
    '3200-3500', '3500-3800', '3800-4100', '>4100',
];

const DIET_TYPE_OPTIONS = [
    'omnivore', 'vegan', 'vegetarian', 'carnivore', 'pescatarian',
    'mediterranean', 'ketogenic', 'paleo', 'gluten_free', 'lactose_free',
];

/** Computes average daily kcal for a template's dietPlan. Returns 0 if no food data. */
const getAvgKcal = (dietPlan) => {
    let total = 0, days = 0;
    Object.values(dietPlan ?? {}).forEach(day => {
        if (typeof day !== 'object' || !day) return;
        let dayKcal = 0, hasFood = false;
        Object.values(day).forEach(meal => {
            if (!Array.isArray(meal?.foods)) return;
            meal.foods.forEach(f => { dayKcal += parseFloat(f.kcal ?? 0); hasFood = true; });
        });
        if (hasFood) { total += dayKcal; days++; }
    });
    return days > 0 ? Math.round(total / days) : 0;
};

const matchesBucket = (kcal, bucket) => {
    if (!bucket) return true;
    if (bucket === '<800')  return kcal < 800;
    if (bucket === '>4100') return kcal > 4100;
    const [lo, hi] = bucket.split('-').map(Number);
    return kcal >= lo && kcal < hi;
};

const intoleranceSx = {
    flex: 1,
    '& .MuiOutlinedInput-root': {
        '&:hover fieldset': { borderColor: 'var(--secondary-color)' },
        '&.Mui-focused fieldset': { borderColor: 'var(--primary-color)' },
    },
    '& .MuiInputLabel-root.Mui-focused': { color: 'var(--primary-color)' },
    '& .MuiChip-root': {
        height: 20, fontSize: 11,
        backgroundColor: 'rgba(253, 71, 1, 0.1)',
        color: 'var(--primary-color)',
        '& .MuiChip-deleteIcon': { color: 'var(--primary-color)', fontSize: 14 },
    },
};

const illnessSx = {
    flex: 1,
    '& .MuiOutlinedInput-root': {
        '&:hover fieldset': { borderColor: '#93c5fd' },
        '&.Mui-focused fieldset': { borderColor: '#3b82f6' },
    },
    '& .MuiInputLabel-root.Mui-focused': { color: '#3b82f6' },
    '& .MuiChip-root': {
        height: 20, fontSize: 11,
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        color: '#3b82f6',
        '& .MuiChip-deleteIcon': { color: '#3b82f6', fontSize: 14 },
    },
};

const caloricSx = {
    minWidth: 150,
    '& .MuiOutlinedInput-root': {
        '&:hover fieldset': { borderColor: '#a3a3a3' },
        '&.Mui-focused fieldset': { borderColor: '#64748b' },
    },
    '& .MuiInputLabel-root.Mui-focused': { color: '#64748b' },
};

const TemplatePickerModal = ({ setShowModal, onSelect }) => {
    const { t, i18n } = useTranslation();
    const lang = i18n.language?.startsWith('it') ? 'it' : 'en';

    const [templates,          setTemplates]          = useState([]);
    const [loading,            setLoading]            = useState(true);
    const [allIntolerances,    setAllIntolerances]    = useState([]);
    const [allIllnesses,       setAllIllnesses]       = useState([]);
    const [intoleranceFilter,  setIntoleranceFilter]  = useState([]);
    const [illnessFilter,      setIllnessFilter]      = useState([]);
    const [caloricFilter,      setCaloricFilter]      = useState('');
    const [dietTypeFilter,     setDietTypeFilter]     = useState('');

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [templatesRes, intolerancesRes, illnessesRes] = await Promise.all([
                    axios.get(`${process.env.REACT_APP_BACKEND_URL}/diet-templates`,    { withCredentials: true }),
                    axios.get(`${process.env.REACT_APP_BACKEND_URL}/food-intolerances`, { withCredentials: true }),
                    axios.get(`${process.env.REACT_APP_BACKEND_URL}/illnesses`,         { withCredentials: true }),
                ]);
                setTemplates(templatesRes.data);
                setAllIntolerances(intolerancesRes.data);
                setAllIllnesses(illnessesRes.data);
            } catch (error) {
                console.error('Error fetching template picker data:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    const filteredTemplates = templates.filter(tmpl => {
        if (intoleranceFilter.length > 0) {
            const ids = (tmpl.intolerances ?? []).map(i => i._id);
            if (!intoleranceFilter.every(f => ids.includes(f._id))) return false;
        }
        if (illnessFilter.length > 0) {
            const ids = (tmpl.illnesses ?? []).map(i => i._id);
            if (!illnessFilter.every(f => ids.includes(f._id))) return false;
        }
        if (caloricFilter) {
            const avgKcal = getAvgKcal(tmpl.dietPlan);
            if (avgKcal === 0 || !matchesBucket(avgKcal, caloricFilter)) return false;
        }
        if (dietTypeFilter) {
            if ((tmpl.dietType ?? 'omnivore') !== dietTypeFilter) return false;
        }
        return true;
    });

    return (
        <div className="modal-overlay">
            <div id='template-picker-modal' className="modal-content" data-tour="tpm-modal">
                <div id='modal-title'>
                    <h2>{t('select-template')}</h2>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <TourButton steps={getTemplatePickerModalSteps(t)} iconOnly />
                        <div id='close-modal-icon-wrapper' onClick={() => setShowModal(false)}>
                            <CloseIcon />
                        </div>
                    </div>
                </div>

                <div id='tpm-filters'>
                    <div id='tpm-filters-row1'>
                        <Autocomplete
                            multiple size="small"
                            options={allIntolerances}
                            value={intoleranceFilter}
                            onChange={(_, v) => setIntoleranceFilter(v)}
                            getOptionLabel={(o) => o.name?.[lang] ?? ''}
                            isOptionEqualToValue={(o, v) => o._id === v._id}
                            renderInput={(params) => (
                                <TextField {...params} label={t('intolerances')} />
                            )}
                            sx={intoleranceSx}
                            data-tour="tpm-intolerances"
                        />
                        <Autocomplete
                            multiple size="small"
                            options={allIllnesses}
                            value={illnessFilter}
                            onChange={(_, v) => setIllnessFilter(v)}
                            getOptionLabel={(o) => o.name?.[lang] ?? ''}
                            isOptionEqualToValue={(o, v) => o._id === v._id}
                            renderInput={(params) => (
                                <TextField {...params} label={t('illnesses')} />
                            )}
                            sx={illnessSx}
                            data-tour="tpm-illnesses"
                        />
                    </div>
                    <div id='tpm-filters-row2'>
                        <FormControl size="small" sx={caloricSx} data-tour="tpm-caloric">
                            <InputLabel>{t('caloric-range')} (kcal)</InputLabel>
                            <Select
                                value={caloricFilter}
                                label={`${t('caloric-range')} (kcal)`}
                                onChange={(e) => setCaloricFilter(e.target.value)}
                            >
                                <MenuItem value=""><em>{t('all')}</em></MenuItem>
                                {CALORIC_BUCKETS.map(b => (
                                    <MenuItem key={b} value={b}>{b} kcal</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl size="small" sx={caloricSx}>
                            <InputLabel>{t('diet-type')}</InputLabel>
                            <Select
                                value={dietTypeFilter}
                                label={t('diet-type')}
                                onChange={(e) => setDietTypeFilter(e.target.value)}
                            >
                                <MenuItem value=""><em>{t('all')}</em></MenuItem>
                                {DIET_TYPE_OPTIONS.map(type => (
                                    <MenuItem key={type} value={type}>{t(type)}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </div>
                </div>

                <div id='template-picker-body' data-tour="tpm-list">
                    {loading ? (
                        <CircularProgress sx={{ color: 'var(--primary-color)', mt: 2 }} />
                    ) : filteredTemplates.length === 0 ? (
                        <p id='no-templates-msg'>{t('no-templates-found')}</p>
                    ) : (
                        <div id='template-list'>
                            {filteredTemplates.map(tmpl => (
                                <TemplateCard
                                    key={tmpl._id}
                                    template={tmpl}
                                    onClick={() => onSelect(tmpl)}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TemplatePickerModal;
